import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const AUTH_BG = "https://images.unsplash.com/photo-1587301552314-4dabb468fdd5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Login() {
  const { login, demoLogin } = useFgp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("error") === "google") {
      toast.error("Google sign-in didn't complete. Please try again.");
      window.history.replaceState(null, "", "/login");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return toast.error("Enter your email and password");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const tryDemo = async () => {
    setLoading(true);
    try {
      await demoLogin();
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err.message || "Demo unavailable");
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 relative bg-slate-900">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" data-testid="login-logo-home-link"><Logo light /></Link>
          <div>
            <h2 className="font-heading text-3xl font-semibold leading-tight max-w-md">Total operational control for elite support teams.</h2>
            <p className="mt-4 text-slate-300 max-w-md">Manage every principal's people, documents, renewals, calendar and tasks — in one trusted place.</p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Link to="/" data-testid="login-logo-home-link-mobile"><Logo /></Link></div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Welcome back</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to your Swishr workspace.</p>

          <button onClick={googleLogin} data-testid="google-login-btn" type="button"
            className="mt-8 w-full flex items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
            Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com" data-testid="login-email" className="pl-9 bg-white" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Link to="/reset-password" className="text-xs text-neutral-600 hover:underline" data-testid="forgot-link">Forgot?</Link>
              </div>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" data-testid="login-password" className="pl-9 bg-white" />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-neutral-600 hover:bg-neutral-700 gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>

          <button onClick={tryDemo} disabled={loading} data-testid="demo-login-btn" type="button"
            className="mt-4 w-full rounded-lg bg-neutral-50 border border-neutral-100 px-3 py-2 text-xs text-neutral-700 hover:bg-neutral-100 transition-colors">
            Just exploring? <strong>Try the demo workspace →</strong>
          </button>

          <p className="mt-6 text-sm text-slate-500 text-center">
            New to Swishr Central? <Link to="/register" className="text-neutral-600 font-medium hover:underline" data-testid="to-register-link">Create an account</Link>
          </p>
          <p className="mt-2 text-sm text-slate-400 text-center">
            <Link to="/" className="hover:text-slate-600">← Back to home</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
